// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

export interface SowDocxInput {
  projectName: string;
  address: string;
  date: string;
  tradeName: string;
  tradeContact: string;
  divisionName: string;
  sections: {
    scopeItems: string[];
    miscItems: string[];
    documentList: string;
    submittals: string[];
    exclusions: string[];
    referenceSpecs: string[];
    schedule: { start: string; end: string; notes: string };
    allowances: string[];
    alternates: { description: string; value: string }[];
    unitPrices: string[];
    laborRates: string[];
    recapitulation: { description: string; value: string }[];
    mobilizations: number;
    testingInspection: string[];
  };
}

export interface BidFormInput {
  projectName: string;
  address: string;
  dueDate: string;
  divisionName: string;
  tradeName: string;
  scopeItems: string[];
  alternates: { description: string }[];
  unitPriceItems: string[];
  laborCategories: string[];
  proposals: { name: string }[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function slugify(s: string) {
  return s.replace(/[^a-z0-9]/gi, "_").slice(0, 40);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
}

// ─── SOW Document ────────────────────────────────────────────────────────────

export async function generateSowDocx(input: SowDocxInput): Promise<void> {
  const {
    Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
    WidthType, BorderStyle, AlignmentType, HeadingLevel,
    convertInchesToTwip, UnderlineType,
  } = await import("docx");

  const FONT_NAME = "Calibri";
  const COLOR_HEADER = "1A3A5C";
  const COLOR_ACCENT = "1A7A6E";
  const COLOR_BORDER = "C0C8D0";

  // ─── Border / cell helpers ────
  const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  const thinBorder = { style: BorderStyle.SINGLE, size: 6, color: COLOR_BORDER };
  const accentBorder = { style: BorderStyle.SINGLE, size: 12, color: COLOR_ACCENT };

  const allBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
  const noBorders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };
  const bottomBorderOnly = { top: noBorder, bottom: thinBorder, left: noBorder, right: noBorder };
  const topBorderOnly = { top: accentBorder, bottom: noBorder, left: noBorder, right: noBorder };

  // ─── Paragraph helpers ────
  const spacer = (before = 80, after = 80) =>
    new Paragraph({ text: "", spacing: { before, after } });

  const rule = () =>
    new Paragraph({
      children: [new TextRun({ text: "", size: 4 })],
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: COLOR_BORDER } },
      spacing: { before: 60, after: 60 },
    });

  const pageTitle = (text: string) =>
    new Paragraph({
      children: [new TextRun({ text, bold: true, size: 32, color: COLOR_HEADER, font: FONT_NAME })],
      spacing: { before: 0, after: 100 },
    });

  const subtitle = (text: string) =>
    new Paragraph({
      children: [new TextRun({ text, size: 22, color: "555555", font: FONT_NAME })],
      spacing: { before: 0, after: 60 },
    });

  const sectionHeader = (num: number, title: string) =>
    new Paragraph({
      children: [
        new TextRun({ text: `${num}. ${title.toUpperCase()}`, bold: true, size: 22, color: COLOR_ACCENT, font: FONT_NAME, characterSpacing: 40 }),
      ],
      border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: COLOR_ACCENT } },
      spacing: { before: 280, after: 120 },
    });

  const numberedItem = (num: number, text: string) =>
    new Paragraph({
      children: [
        new TextRun({ text: `${num}.`, bold: true, size: 20, font: FONT_NAME }),
        new TextRun({ text: `  ${text}`, size: 20, font: FONT_NAME }),
      ],
      indent: { left: convertInchesToTwip(0.25) },
      spacing: { before: 60, after: 60 },
    });

  const letterItem = (letter: string, text: string) =>
    new Paragraph({
      children: [
        new TextRun({ text: `${letter}.`, size: 20, font: FONT_NAME, color: "555555" }),
        new TextRun({ text: `  ${text}`, size: 20, font: FONT_NAME }),
      ],
      indent: { left: convertInchesToTwip(0.5) },
      spacing: { before: 40, after: 40 },
    });

  const metaRow = (label: string, value: string) =>
    new Paragraph({
      children: [
        new TextRun({ text: `${label}: `, bold: true, size: 20, font: FONT_NAME }),
        new TextRun({ text: value || "—", size: 20, font: FONT_NAME }),
      ],
      spacing: { before: 40, after: 40 },
    });

  const emptyLine = (label: string) =>
    new Paragraph({
      children: [
        new TextRun({ text: `${label}: `, bold: true, size: 20, font: FONT_NAME }),
        new TextRun({ text: "____________________________________________________________", size: 20, font: FONT_NAME, color: "AAAAAA" }),
      ],
      spacing: { before: 40, after: 40 },
    });

  const bodyText = (text: string) =>
    new Paragraph({
      children: [new TextRun({ text: text || "(none)", size: 20, font: FONT_NAME })],
      spacing: { before: 40, after: 40 },
      indent: { left: convertInchesToTwip(0.25) },
    });

  // ─── Recapitulation table ────
  const recapTable = (rows: { description: string; value: string }[]) => {
    const headerRow = new TableRow({
      children: [
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: "Description", bold: true, size: 20, font: FONT_NAME, color: "FFFFFF" })], spacing: { before: 60, after: 60 }, indent: { left: 80 } })],
          borders: allBorders,
          shading: { fill: COLOR_ACCENT },
          width: { size: 70, type: WidthType.PERCENTAGE },
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: "Value", bold: true, size: 20, font: FONT_NAME, color: "FFFFFF" })], spacing: { before: 60, after: 60 }, alignment: AlignmentType.RIGHT, indent: { right: 80 } })],
          borders: allBorders,
          shading: { fill: COLOR_ACCENT },
          width: { size: 30, type: WidthType.PERCENTAGE },
        }),
      ],
      tableHeader: true,
    });

    const dataRows = rows.map((r, i) =>
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: r.description || `—`, size: 20, font: FONT_NAME })], spacing: { before: 60, after: 60 }, indent: { left: 80 } })],
            borders: allBorders,
            shading: { fill: i % 2 === 0 ? "F7F9FB" : "FFFFFF" },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: r.value || "$ ___________", size: 20, font: FONT_NAME })], spacing: { before: 60, after: 60 }, alignment: AlignmentType.RIGHT, indent: { right: 80 } })],
            borders: allBorders,
            shading: { fill: i % 2 === 0 ? "F7F9FB" : "FFFFFF" },
          }),
        ],
      })
    );

    if (rows.length > 1) {
      dataRows.push(
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: "TOTAL BASE BID", bold: true, size: 20, font: FONT_NAME })], spacing: { before: 80, after: 80 }, indent: { left: 80 } })],
              borders: { ...allBorders, top: accentBorder },
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: "$ ___________", bold: true, size: 20, font: FONT_NAME })], spacing: { before: 80, after: 80 }, alignment: AlignmentType.RIGHT, indent: { right: 80 } })],
              borders: { ...allBorders, top: accentBorder },
            }),
          ],
        })
      );
    }

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows],
    });
  };

  // ─── Build document children ────
  const children: any[] = [];
  const s = input.sections;

  // Header block
  children.push(pageTitle(input.projectName || "Construction Project"));
  children.push(subtitle(input.address || ""));
  children.push(metaRow("Date", input.date || new Date().toLocaleDateString()));
  children.push(metaRow("Division", input.divisionName));
  children.push(metaRow("Trade Contractor", input.tradeName || "—"));
  if (input.tradeContact) children.push(metaRow("Contact", input.tradeContact));

  children.push(rule());
  children.push(
    new Paragraph({
      children: [new TextRun({ text: `SPECIAL REQUIREMENTS — SCOPE OF WORK — ${(input.divisionName || "").toUpperCase()}`, bold: true, size: 24, color: COLOR_HEADER, font: FONT_NAME, characterSpacing: 20 })],
      spacing: { before: 80, after: 160 },
    })
  );

  // 1. Scope of Work
  children.push(sectionHeader(1, "Scope of Work"));
  (s.scopeItems || []).forEach((item, i) => children.push(numberedItem(i + 1, item)));
  if (!s.scopeItems?.length) children.push(bodyText("(No scope items listed)"));

  // 2. Misc. Work Included
  children.push(sectionHeader(2, "Misc. Work Included"));
  (s.miscItems || []).forEach((item, i) => {
    const letter = String.fromCharCode(97 + i);
    children.push(letterItem(letter, item));
  });
  if (!s.miscItems?.length) children.push(bodyText("(None)"));

  // 3. Document List
  children.push(sectionHeader(3, "Document List"));
  if (s.documentList?.trim()) {
    s.documentList.split("\n").forEach(line => {
      if (line.trim()) children.push(bodyText(line.trim()));
    });
  } else {
    children.push(bodyText("Contract documents issued for construction, including all architectural, structural, MEP, and civil drawings and specifications current at time of bid."));
  }

  // 4. Submittals
  children.push(sectionHeader(4, "Submittals"));
  (s.submittals || []).forEach((item, i) => {
    const letter = String.fromCharCode(97 + i);
    children.push(letterItem(letter, item));
  });
  if (!s.submittals?.length) children.push(bodyText("(None)"));

  // 5. Exclusions
  children.push(sectionHeader(5, "Exclusions"));
  (s.exclusions || []).forEach((item, i) => {
    const letter = String.fromCharCode(97 + i);
    children.push(letterItem(letter, item));
  });
  if (!s.exclusions?.length) children.push(bodyText("(None)"));

  // 6. Reference Specifications
  children.push(sectionHeader(6, "Reference Specifications"));
  (s.referenceSpecs || []).forEach((item, i) => {
    const letter = String.fromCharCode(97 + i);
    children.push(letterItem(letter, item));
  });
  if (!s.referenceSpecs?.length) children.push(bodyText("(None)"));

  // 7. Schedule
  children.push(sectionHeader(7, "Schedule"));
  children.push(metaRow("Start Date", s.schedule?.start || "—"));
  children.push(metaRow("Completion Date", s.schedule?.end || "—"));
  children.push(metaRow("Number of Mobilizations", String(s.mobilizations ?? 1)));
  if (s.schedule?.notes) children.push(bodyText(s.schedule.notes));

  // 8. Allowances
  children.push(sectionHeader(8, "Allowances"));
  (s.allowances || []).forEach((item, i) => {
    const letter = String.fromCharCode(97 + i);
    children.push(letterItem(letter, item));
  });
  if (!s.allowances?.length) children.push(bodyText("No allowances included in this scope."));

  // 9. Alternates
  children.push(sectionHeader(9, "Alternates"));
  (s.alternates || []).forEach((alt, i) => {
    const letter = String.fromCharCode(97 + i);
    children.push(letterItem(letter, `${alt.description}${alt.value ? `  — ${alt.value}` : "  — $ ___________"}`));
  });
  if (!s.alternates?.length) children.push(bodyText("No alternates included in this scope."));

  // 10. Unit Prices
  children.push(sectionHeader(10, "Unit Prices"));
  (s.unitPrices || []).forEach((item, i) => {
    const letter = String.fromCharCode(97 + i);
    children.push(letterItem(letter, item));
  });
  if (!s.unitPrices?.length) children.push(bodyText("No unit prices submitted."));

  // 11. Labor Rates
  children.push(sectionHeader(11, "Labor Rates"));
  (s.laborRates || []).forEach((item, i) => {
    const letter = String.fromCharCode(97 + i);
    children.push(letterItem(letter, item));
  });
  if (!s.laborRates?.length) children.push(bodyText("No labor rates submitted."));

  // 12. Contract Recapitulation
  children.push(sectionHeader(12, "Contract Recapitulation"));
  if (s.recapitulation?.length) {
    children.push(spacer(40, 80));
    children.push(recapTable(s.recapitulation));
  } else {
    children.push(spacer(40, 40));
    children.push(recapTable([{ description: "Base Bid — " + input.divisionName, value: "$ ___________" }]));
  }

  // Testing & Inspection (bonus section if present)
  if (s.testingInspection?.length) {
    children.push(sectionHeader(13, "Testing & Inspection"));
    s.testingInspection.forEach((item, i) => {
      const letter = String.fromCharCode(97 + i);
      children.push(letterItem(letter, item));
    });
  }

  children.push(spacer(160, 80));
  children.push(rule());
  children.push(
    new Paragraph({
      children: [new TextRun({ text: "END OF SCOPE OF WORK", bold: true, size: 18, color: "888888", font: FONT_NAME, characterSpacing: 60 })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 80, after: 80 },
    })
  );

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(1),
            right: convertInchesToTwip(1),
            bottom: convertInchesToTwip(1),
            left: convertInchesToTwip(1),
          },
        },
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, `${slugify(input.projectName)}_SOW_${slugify(input.divisionName)}.docx`);
}

// ─── Bid Form Document ────────────────────────────────────────────────────────

export async function generateBidFormDocx(input: BidFormInput): Promise<void> {
  const {
    Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
    WidthType, BorderStyle, AlignmentType, convertInchesToTwip,
  } = await import("docx");

  const FONT_NAME = "Calibri";
  const COLOR_HEADER = "1A3A5C";
  const COLOR_ACCENT = "1A7A6E";
  const COLOR_BORDER = "C0C8D0";

  const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  const thinBorder = { style: BorderStyle.SINGLE, size: 6, color: COLOR_BORDER };
  const accentBorder = { style: BorderStyle.SINGLE, size: 12, color: COLOR_ACCENT };
  const allBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

  const spacer = (before = 80, after = 80) =>
    new Paragraph({ text: "", spacing: { before, after } });

  const rule = () =>
    new Paragraph({
      children: [new TextRun({ text: "", size: 4 })],
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: COLOR_BORDER } },
      spacing: { before: 60, after: 60 },
    });

  const pageTitle = (text: string) =>
    new Paragraph({
      children: [new TextRun({ text, bold: true, size: 34, color: COLOR_HEADER, font: FONT_NAME })],
      spacing: { before: 0, after: 80 },
    });

  const sectionHeader = (num: number, title: string) =>
    new Paragraph({
      children: [new TextRun({ text: `SECTION ${num}: ${title.toUpperCase()}`, bold: true, size: 20, color: COLOR_ACCENT, font: FONT_NAME, characterSpacing: 40 })],
      border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: COLOR_ACCENT } },
      spacing: { before: 260, after: 120 },
    });

  const metaRow = (label: string, value: string) =>
    new Paragraph({
      children: [
        new TextRun({ text: `${label}: `, bold: true, size: 20, font: FONT_NAME }),
        new TextRun({ text: value || "—", size: 20, font: FONT_NAME }),
      ],
      spacing: { before: 40, after: 40 },
    });

  const fillLine = (label: string) =>
    new Paragraph({
      children: [
        new TextRun({ text: `${label}: `, bold: true, size: 20, font: FONT_NAME }),
        new TextRun({ text: "_____________________________________________________________", size: 20, font: FONT_NAME, color: "BBBBBB" }),
      ],
      spacing: { before: 80, after: 80 },
    });

  const bodyText = (text: string) =>
    new Paragraph({
      children: [new TextRun({ text, size: 20, font: FONT_NAME })],
      spacing: { before: 40, after: 40 },
    });

  // ─── Tables ────

  const makeSimpleTable = (
    headers: string[],
    widths: number[],
    rows: string[][]
  ) => {
    const headerRow = new TableRow({
      children: headers.map((h, i) =>
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, size: 20, font: FONT_NAME, color: "FFFFFF" })], spacing: { before: 60, after: 60 }, indent: { left: 80 } })],
          borders: allBorders,
          shading: { fill: COLOR_ACCENT },
          width: { size: widths[i], type: WidthType.PERCENTAGE },
        })
      ),
      tableHeader: true,
    });

    const dataRows = rows.map((row, ri) =>
      new TableRow({
        children: row.map((cell, ci) =>
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: cell, size: 20, font: FONT_NAME })], spacing: { before: 60, after: 60 }, indent: { left: 80 } })],
            borders: allBorders,
            shading: { fill: ri % 2 === 0 ? "F7F9FB" : "FFFFFF" },
            width: { size: widths[ci], type: WidthType.PERCENTAGE },
          })
        ),
      })
    );

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows],
    });
  };

  const children: any[] = [];

  // ─── Document Header ────
  children.push(pageTitle(`BID FORM — ${input.divisionName || "Trade Work"}`));
  children.push(metaRow("Project", input.projectName || "—"));
  children.push(metaRow("Address", input.address || "—"));
  children.push(metaRow("Bid Due Date", input.dueDate || "—"));
  children.push(metaRow("To", "Pinemar Construction"));
  children.push(metaRow("For Division", input.divisionName || "—"));
  children.push(rule());

  // ─── Section 1: Bidder Information ────
  children.push(sectionHeader(1, "Bidder Information"));
  children.push(fillLine("Company Name"));
  children.push(fillLine("Contact Name"));
  children.push(fillLine("Phone"));
  children.push(fillLine("Email"));
  children.push(fillLine("License Number"));
  children.push(fillLine("Contractor's Insurance Carrier"));
  children.push(fillLine("Workers' Comp. Carrier"));

  // ─── Section 2: Base Bid ────
  children.push(sectionHeader(2, "Base Bid"));
  children.push(
    new Paragraph({
      children: [new TextRun({ text: "BASE BID AMOUNT:   $  ____________________________", bold: true, size: 28, font: FONT_NAME, color: COLOR_HEADER })],
      spacing: { before: 80, after: 40 },
    })
  );
  children.push(
    new Paragraph({
      children: [new TextRun({ text: "(Written Amount): _______________________________________________________________", size: 20, font: FONT_NAME })],
      spacing: { before: 40, after: 80 },
    })
  );

  // ─── Section 3: Scope Inclusions/Exclusions ────
  children.push(sectionHeader(3, "Scope Inclusions / Exclusions"));
  children.push(bodyText("Bidder shall indicate for each item below whether it is Included in the base bid or Excluded/By Others."));
  children.push(spacer(40, 80));

  const scopeTableRows: string[][] = (input.scopeItems || []).map(item => [item, "", ""]);
  if (scopeTableRows.length === 0) {
    scopeTableRows.push(["(see scope of work document for full scope description)", "", ""]);
  }
  children.push(makeSimpleTable(
    ["Scope Item", "Included ✓", "Excluded / By Others"],
    [68, 16, 16],
    scopeTableRows
  ));

  // ─── Section 4: Alternates ────
  children.push(sectionHeader(4, "Alternates"));
  if (input.alternates?.length) {
    const altRows = input.alternates.map((a, i) => [`Alt ${i + 1}: ${a.description}`, "Add / Deduct", "$ ___________"]);
    children.push(makeSimpleTable(
      ["Alternate Description", "Add / Deduct", "Amount"],
      [58, 20, 22],
      altRows
    ));
  } else {
    children.push(bodyText("No alternates requested."));
  }

  // ─── Section 5: Unit Prices ────
  children.push(sectionHeader(5, "Unit Prices"));
  const unitPriceItems = input.unitPriceItems?.length
    ? input.unitPriceItems
    : ["Removal and replacement of unforeseen/concealed deteriorated substrate (per SF)", "Additional scope not covered by base bid (per LS)"];
  const unitRows = unitPriceItems.map(item => [item, "", "$ ___________ /unit"]);
  children.push(makeSimpleTable(
    ["Item Description", "Unit", "$/Unit"],
    [60, 15, 25],
    unitRows
  ));

  // ─── Section 6: Labor Rates ────
  children.push(sectionHeader(6, "Labor Rates"));
  const labCats = input.laborCategories?.length
    ? input.laborCategories
    : ["Journeyman", "Foreman", "Laborer"];
  const laborRows = labCats.map(cat => [cat, "$ _____ /hr", "$ _____ /hr"]);
  children.push(makeSimpleTable(
    ["Labor Category", "ST ($/hr)", "OT ($/hr)"],
    [60, 20, 20],
    laborRows
  ));

  // ─── Section 7: Schedule ────
  children.push(sectionHeader(7, "Schedule"));
  children.push(fillLine("Proposed Start Date"));
  children.push(fillLine("Proposed Completion Date"));
  children.push(fillLine("Crew Size (number of workers on site daily)"));
  children.push(fillLine("Number of Mobilizations"));
  children.push(fillLine("Any known schedule constraints or long-lead items"));

  // ─── Section 8: Value Breakdown / Recapitulation ────
  children.push(sectionHeader(8, "Value Breakdown / Recapitulation"));
  children.push(bodyText("Please provide a breakdown of your bid by proposal package or building area:"));
  children.push(spacer(40, 80));
  const proposalNames = input.proposals?.map(p => p.name) || [];
  if (proposalNames.length === 0) proposalNames.push("Base Bid");
  const recapRows = [
    ...proposalNames.map(n => [n, "$ ___________"]),
    ["TOTAL BASE BID", "$ ___________"],
  ];
  children.push(makeSimpleTable(
    ["Description / Package", "Value"],
    [70, 30],
    recapRows
  ));

  // ─── Section 9: Qualifications & Exclusions ────
  children.push(sectionHeader(9, "Qualifications & Exclusions"));
  children.push(bodyText("Please list any qualifications, exclusions, assumptions, or clarifications to your bid:"));
  for (let i = 0; i < 5; i++) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: "_______________________________________________________________________________________________________________________________", size: 20, font: FONT_NAME, color: "CCCCCC" })],
        spacing: { before: 80, after: 40 },
      })
    );
  }

  // ─── Section 10: Signature Block ────
  children.push(sectionHeader(10, "Signature"));
  children.push(bodyText("The undersigned hereby certifies that this bid is genuine and not made in the interest of or on behalf of any undisclosed person, firm, or corporation, and that this bid has been submitted in good faith."));
  children.push(spacer(80, 40));
  children.push(fillLine("Company Name"));
  children.push(fillLine("Authorized Signature"));
  children.push(fillLine("Printed Name"));
  children.push(fillLine("Title"));
  children.push(fillLine("Date"));

  children.push(spacer(120, 60));
  children.push(rule());
  children.push(
    new Paragraph({
      children: [new TextRun({ text: "END OF BID FORM", bold: true, size: 18, color: "888888", font: FONT_NAME, characterSpacing: 60 })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 80, after: 80 },
    })
  );

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(1),
            right: convertInchesToTwip(1),
            bottom: convertInchesToTwip(1),
            left: convertInchesToTwip(1),
          },
        },
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, `${slugify(input.projectName)}_BidForm_${slugify(input.divisionName)}.docx`);
}
